import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { TableSortIcon } from '~components/generic/table/TableSortIcon';
import LoadingBlocker from '~components/loadingBlocker/LoadingBlocker';
import { queries } from '~extension/favourite/api/Queries';
import useLabels from '~hooks/useLabels';
import useFavouriteService from '../hook/useFavouriteService';
import type FavouriteDto from '../model/FavouriteDto';

interface TableProps {
  columns: any;
  data: any;
}

function Table({ columns, data }: TableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'title',
      desc: false,
    },
  ]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <>
      <div className="table-frame">
        <table className="c-content-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <TableSortIcon type="asc" />,
                            desc: <TableSortIcon type="desc" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export type TitleCellProps = {
  value: string;
  row: { original: { url: string; published: boolean; description: string | null } };
};

function TitleCell({ value, row }: TitleCellProps) {
  let title = <a href={row.original.url}>{value}</a>;
  if (row.original?.published === false) {
    title = (
      <>
        <strong>{value as string}</strong> (deleted)
      </>
    );
  }
  let description = <></>;
  if (row.original.description !== null) {
    description = <>{row.original.description}</>;
  }

  return (
    <>
      {title} <br /> {description}
    </>
  );
}

export function FavouriteList() {
  const { removeContentFavourite } = useFavouriteService();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [favourites, setFavourites] = useState<FavouriteDto[]>(null);

  const { hlFavouritesContent, hlFavouritesLastUpdated, hlFavouritesTitle } = useLabels([
    'hlFavouritesContent',
    'hlFavouritesLastUpdated',
    'hlFavouritesTitle',
  ]);

  const { data: queryData } = useQuery({
    ...queries.favourites.getFavourites(),
    enabled: !Boolean(favourites),
  });

  useEffect(() => {
    if (queryData === undefined) return;
    setFavourites(queryData);
    setIsLoading(false);
  }, [queryData]);

  const handleRemoveFavourite = useCallback(
    async (key: string) => {
      setIsLoading(true);

      const res = await removeContentFavourite(key);
      if (res) setFavourites(res);

      setIsLoading(false);
    },
    [removeContentFavourite],
  );

  const columns = useMemo(
    () => [
      {
        header: <></>,
        accessorKey: 'favourites',
        enableSorting: true,
        columns: [
          {
            header: () => <span>{hlFavouritesTitle}</span>,
            accessorKey: 'title',
            cell: ({ getValue, row }) => <TitleCell value={getValue()} row={row} />,
          },
          {
            header: () => <span>{hlFavouritesLastUpdated}</span>,
            accessorKey: 'modifiedAt',
            cell: ({ getValue }) => <FormattedDate value={getValue()} year="numeric" month="long" day="numeric" />,
          },
          {
            header: () => <span></span>,
            accessorKey: 'actions',
            cell: ({ row }) => <a onClick={() => handleRemoveFavourite?.(row.original.key)}>x</a>,
            enableSorting: false,
          },
        ],
      },
    ],
    [handleRemoveFavourite, hlFavouritesLastUpdated, hlFavouritesTitle],
  );

  return (
    <>
      <h2>{hlFavouritesContent}</h2>
      <span>
        <FormattedMessage
          id="msgFavouritesContent"
          values={{
            br: <br />,
          }}
        />
      </span>
      <LoadingBlocker loading={isLoading}>
        {favourites && favourites.length > 0 && <Table columns={columns} data={favourites} />}
        {(!favourites || favourites.length === 0) && (
          <>
            <br />
            <FormattedMessage
              id="msgFavouritesEmpty"
              defaultMessage="No Favourites"
              values={{
                a: chunks => <a href="/brandguide/">{chunks}</a>,
              }}
            />
            <br />
            <br />
          </>
        )}
      </LoadingBlocker>
    </>
  );
}
